import Component from "vue-class-component";
import Vue from "vue";
import { Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { EpgItem, MyChannel } from "@/services/apiService/api.types";
import {
  OverlayGetter,
  OverlayMutation,
  OverlaySection
} from "@/components/overlay/types";
import { ChannelChangeGetter } from "@/components/channelChange/types";
import { Watch } from "vue-property-decorator";

@Component
export class EpgHandler extends Vue {
  @Mutation(OverlayMutation.SetActiveEpgItem, { namespace: Namespace.Overlay })
  setActiveEpgItem!: (epgItem: EpgItem) => void;

  @Getter(OverlayGetter.GetActiveEpgItem, {
    namespace: Namespace.Overlay
  })
  getActiveEpgItem!: EpgItem;

  @Getter(ChannelChangeGetter.GetOffset, {
    namespace: Namespace.ChannelChange
  })
  getOffset!: number;

  @Getter(OverlayGetter.GetChannelEpg, {
    namespace: Namespace.Overlay
  })
  getChannelEpg!: EpgItem[];

  @Getter(ChannelChangeGetter.GetChannelId, {
    namespace: Namespace.ChannelChange
  })
  getChannelId!: number;

  @Getter(OverlayGetter.GetOverlayOpen, {
    namespace: Namespace.Overlay
  })
  getOverlayMenuOpen!: OverlaySection;

  channelList: MyChannel[] = [];

  //vars for progress bar
  secondsElapsed = 0;
  secondsPerPercent = 0;

  /*
   *  mockData is used to indicate that there should be a new activeEpgItem(the one from the previewed/focused channel from ChannelList.vue)
   *  and therefore we should get the active epgItem from that channels epg(we essentially replace all related data without triggering channel change)
   */
  updateActiveEpgItem(mockData = false): void {
    dayjs.extend(isBetween);
    const timestamp = mockData
      ? dayjs()
      : dayjs().subtract(this.getOffset, "second"); //current time adjusted for any possible offset(5s offset = 5 seconds before current time)

    //ensuring to only trigger the search if required
    if (
      this.getActiveEpgItem?.channel_id !== this.getChannelId ||
      mockData ||
      !timestamp.isBetween(
        this.getActiveEpgItem.epgStarttime,
        this.getActiveEpgItem.epgEndtime
      )
    ) {
      const activeEpgItem = this.getChannelEpg.find((epgItem: EpgItem) => {
        const epgStartTime = dayjs(epgItem.epgStarttime);
        const epgEndTime = dayjs(epgItem.epgEndtime);

        return timestamp.isBetween(epgStartTime, epgEndTime);
      });
      if (activeEpgItem) {
        this.setActiveEpgItem(activeEpgItem);
      }
    }
  }

  epgItemStartTime(epgItem: EpgItem): string {
    if (dayjs(epgItem.epgStarttime).isValid()) {
      return dayjs(epgItem.epgStarttime).format("HH:mm");
    }
    return "--";
  }

  epgItemEndTime(epgItem: EpgItem): string {
    if (dayjs(epgItem.epgEndtime).isValid()) {
      return dayjs(epgItem.epgEndtime).format("HH:mm");
    }
    return "--";
  }

  epgItemChannelName(epgItem: EpgItem): string {
    for (let i = this.channelList.length - 1; i >= 0; i--) {
      if (this.channelList[i].id === epgItem.channel_id) {
        return this.channelList[i].name;
      }
    }
    return "No info";
  }

  progressPercentage(epgItem: EpgItem): number {
    if (dayjs().isAfter(epgItem.epgEndtime)) {
      return 100;
    }
    if (dayjs().isBefore(epgItem.epgStarttime)) {
      return 0;
    }

    return this.liveProgressPercentage;
  }

  get liveProgressPercentage(): number {
    return Math.round(this.secondsElapsed / this.secondsPerPercent);
  }

  @Watch("getActiveEpgItem", { deep: true, immediate: true })
  handleProgressBar(): void {
    //epgItemDuration
    const duration = dayjs(this.getActiveEpgItem.epgEndtime).diff(
      this.getActiveEpgItem.epgStarttime,
      "second"
    );
    //count of seconds from epgItem beginning to now
    this.secondsElapsed = dayjs().diff(
      this.getActiveEpgItem.epgStarttime,
      "second"
    );
    this.secondsPerPercent = Math.floor(Math.round(duration / 100));
  }
}
